import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Source as SourceIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'on-hold', label: 'On Hold' },
];

const stageOptions = [
  { value: 'Qualification', label: 'Qualification' },
  { value: 'Proposal', label: 'Proposal' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Closed Won', label: 'Closed Won' },
  { value: 'Closed Lost', label: 'Closed Lost' },
];

const sourceOptions = [
  { value: 'Referral', label: 'Referral' },
  { value: 'Web', label: 'Web' },
  { value: 'Event', label: 'Event' },
  { value: 'Cold Call', label: 'Cold Call' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Other', label: 'Other' },
];

const VOICE_FIELD_MAP = {
  title: 'title',
  description: 'description',
  stage: 'stage',
  status: 'status',
  value: 'value',
  currency: 'currency',
  probability: 'probability',
  expectedclosedate: 'expectedCloseDate',
  'expected close date': 'expectedCloseDate',
  actualclosedate: 'actualCloseDate',
  'actual close date': 'actualCloseDate',
  products: 'products',
  source: 'source',
  tags: 'tags',
  notes: 'notes',
};

function OpportunityForm({
  opportunity = null,
  onSave,
  onCancel,
  loading = false,
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    stage: 'Qualification',
    status: 'open',
    value: '',
    currency: 'USD',
    probability: '',
    expectedCloseDate: '',
    actualCloseDate: '',
    products: '',
    source: '',
    tags: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [voiceInput, setVoiceInput] = useState('');
  const [useNlp, setUseNlp] = useState(true);
  const [provider, setProvider] = useState('ollama');
  const recognitionRef = useRef(null);

  // Initialize form with opportunity data
  useEffect(() => {
    if (opportunity) {
      setForm({
        ...opportunity,
        products: (opportunity.products || []).join(', '),
        tags: (opportunity.tags || []).join(', '),
      });
    } else {
      setForm({
        title: '',
        description: '',
        stage: 'Qualification',
        status: 'open',
        value: '',
        currency: 'USD',
        probability: '',
        expectedCloseDate: '',
        actualCloseDate: '',
        products: '',
        source: '',
        tags: '',
        notes: '',
      });
    }
    setErrors({});
  }, [opportunity]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!form.stage.trim()) {
      newErrors.stage = 'Stage is required';
    }

    if (form.value && (isNaN(form.value) || form.value < 0)) {
      newErrors.value = 'Value must be a positive number';
    }

    if (form.probability && (isNaN(form.probability) || form.probability < 0 || form.probability > 100)) {
      newErrors.probability = 'Probability must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const formData = {
      ...form,
      products: form.products ? form.products.split(',').map((product) => product.trim()).filter((product) => product) : [],
      tags: form.tags ? form.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag) : [],
      value: form.value ? parseFloat(form.value) : 0,
      probability: form.probability ? parseFloat(form.probability) : 0,
    };

    onSave(formData);
  };

  const handleVoicePrompt = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = async (event) => {
      const { transcript } = event.results[0][0];
      setVoiceInput(transcript);

      if (useNlp) {
        try {
          const res = await fetch('/api/v1/opportunities/extract-fields', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transcript, provider }),
          });
          const data = await res.json();
          if (data && typeof data === 'object') {
            setForm((prev) => ({ ...prev, ...data }));
          }
        } catch (err) { /* ignore error */ }
      } else {
        const updates = {};
        const parts = transcript.toLowerCase().split(',').map((part) => part.trim());

        parts.forEach((part) => {
          Object.entries(VOICE_FIELD_MAP).forEach(([label, field]) => {
            if (part.startsWith(label)) {
              let value = part.replace(label, '').trim();
              if (field === 'tags' || field === 'products') {
                value = value.split(',').map((t) => t.trim()).filter(Boolean);
              }
              updates[field] = value;
            }
          });
        });

        setForm((prev) => ({ ...prev, ...updates }));
      }
    };

    recognition.onerror = () => {
      setIsListening(false);
      setVoiceInput('');
    };

    recognition.onend = () => {
      setIsListening(false);
      setVoiceInput('');
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Voice Input Section */}
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant={isListening ? 'outlined' : 'contained'}
              color={isListening ? 'error' : 'primary'}
              startIcon={isListening ? <StopIcon /> : <MicIcon />}
              onClick={handleVoicePrompt}
              size="small"
            >
              {isListening ? 'Stop Voice Input' : 'Voice Input'}
            </Button>
            <FormControlLabel
              control={<Switch checked={useNlp} onChange={(e) => setUseNlp(e.target.checked)} color="primary" />}
              label="NLP Voice Input"
              sx={{ ml: 2 }}
            />
            {useNlp && (
              <FormControl size="small" sx={{ minWidth: 160, ml: 2 }}>
                <InputLabel id="provider-label">Model</InputLabel>
                <Select
                  labelId="provider-label"
                  value={provider}
                  label="Model"
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <MenuItem value="ollama">Ollama (Llama 3)</MenuItem>
                  <MenuItem value="llama4">Llama 4 (API)</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                </Select>
              </FormControl>
            )}
            {voiceInput && (
              <Typography variant="body2" color="text.secondary">
                Voice input: "
                {voiceInput}
                "
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessIcon color="primary" />
              <Typography variant="h6">Basic Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Title *"
                  value={form.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.stage}>
                  <InputLabel>Stage *</InputLabel>
                  <Select
                    value={form.stage}
                    onChange={(e) => handleInputChange('stage', e.target.value)}
                    label="Stage *"
                  >
                    {stageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stage && <FormHelperText>{errors.stage}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={form.source}
                    onChange={(e) => handleInputChange('source', e.target.value)}
                    label="Source"
                  >
                    {sourceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Financial Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MoneyIcon color="primary" />
              <Typography variant="h6">Financial Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Value"
                  value={form.value}
                  onChange={(e) => handleInputChange('value', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  error={!!errors.value}
                  helperText={errors.value}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={form.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    label="Currency"
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="CAD">CAD</MenuItem>
                    <MenuItem value="AUD">AUD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Probability (%)"
                  value={form.probability}
                  onChange={(e) => handleInputChange('probability', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  error={!!errors.probability}
                  helperText={errors.probability}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Timeline Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarIcon color="primary" />
              <Typography variant="h6">Timeline</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expected Close Date"
                  value={form.expectedCloseDate}
                  onChange={(e) => handleInputChange('expectedCloseDate', e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Close Date"
                  value={form.actualCloseDate}
                  onChange={(e) => handleInputChange('actualCloseDate', e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Products and Services */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AssessmentIcon color="primary" />
              <Typography variant="h6">Products & Services</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Products/Services (comma separated)"
                  value={form.products}
                  onChange={(e) => handleInputChange('products', e.target.value)}
                  fullWidth
                  helperText="Enter products or services separated by commas"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Additional Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TagIcon color="primary" />
              <Typography variant="h6">Additional Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Tags (comma separated)"
                  value={form.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  fullWidth
                  helperText="Enter tags separated by commas"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Box sx={{
        display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3,
      }}
      >
        <Button onClick={onCancel} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : (opportunity ? 'Update' : 'Save')}
        </Button>
      </Box>
    </form>
  );
}

export default OpportunityForm;
